import { useState } from 'react';
import GlassCard from '../components/ui/GlassCard';
import SettingsHeader from '../components/settings/SettingsHeader';
import CrudCardHeader from '../components/settings/CrudCardHeader';
import PresupuestoTable from '../components/settings/PresupuestoTable';
import TipoConceptoTable from '../components/settings/TipoConceptoTable';
import ConceptoTable from '../components/settings/ConceptoTable';
import CrearPresupuestoModal from '../components/settings/CrearPresupuestoModal';
import ActualizarPresupuestoModal from '../components/settings/ActualizarPresupuestoModal';
import VisualizarPresupuestoModal from '../components/settings/VisualizarPresupuestoModal';
import CrearTipoConceptoModal from '../components/settings/CrearTipoConceptoModal';
import ActualizarTipoConceptoModal from '../components/settings/ActualizarTipoConceptoModal';
import VisualizarTipoConceptoModal from '../components/settings/VisualizarTipoConceptoModal';
import CrearConceptoModal from '../components/settings/CrearConceptoModal';
import ActualizarConceptoModal from '../components/settings/ActualizarConceptoModal';
import VisualizarConceptoModal from '../components/settings/VisualizarConceptoModal';
import ConfirmarToggleModal from '../components/settings/ConfirmarToggleModal';
import { usePresupuestosCrud } from '../hooks/usePresupuestosCrud';
import { useTipoConceptosCrud } from '../hooks/useTipoConceptosCrud';
import { useConceptosCrud } from '../hooks/useConceptosCrud';

const MODAL_NONE = { kind: null };

function Banner({ type, message }) {
  const tones = {
    success: 'border-emerald-300/30 bg-emerald-300/[0.06] text-emerald-200',
    error: 'border-rose-300/30 bg-rose-300/[0.06] text-rose-200',
  };
  return (
    <div
      role="status"
      className={`mt-8 flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm animate-fade-up ${tones[type]}`}
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-[11px] font-bold">
        {type === 'success' ? '✓' : '!'}
      </span>
      {message}
    </div>
  );
}

function Settings() {
  const presupuestoCrud = usePresupuestosCrud();
  const tipoConceptoCrud = useTipoConceptosCrud();
  const conceptoCrud = useConceptosCrud();
  const [presupuestoModal, setPresupuestoModal] = useState(MODAL_NONE);
  const [tipoConceptoModal, setTipoConceptoModal] = useState(MODAL_NONE);
  const [conceptoModal, setConceptoModal] = useState(MODAL_NONE);
  const [banner, setBanner] = useState(null);

  const closePresupuestoModal = () => {
    if (presupuestoCrud.isMutating) return;
    setPresupuestoModal(MODAL_NONE);
  };

  const closeTipoConceptoModal = () => {
    if (tipoConceptoCrud.isMutating) return;
    setTipoConceptoModal(MODAL_NONE);
  };

  const closeConceptoModal = () => {
    if (conceptoCrud.isMutating) return;
    setConceptoModal(MODAL_NONE);
  };

  const handlePresupuestoCreated = async () => {
    await presupuestoCrud.cargar();
    setBanner({ type: 'success', message: 'Se creó el presupuesto con éxito.' });
    setPresupuestoModal(MODAL_NONE);
  };

  const handlePresupuestoUpdated = async () => {
    await presupuestoCrud.cargar();
    setBanner({ type: 'success', message: 'Se actualizó el presupuesto con éxito.' });
    setPresupuestoModal(MODAL_NONE);
  };

  const handlePresupuestoToggle = async () => {
    if (presupuestoModal.kind !== 'toggle') return;
    const result = await presupuestoCrud.toggleEstado(presupuestoModal.item.cdPresupuesto);
    if (result.ok) {
      setBanner({ type: 'success', message: 'Se actualizó el presupuesto con éxito.' });
      await presupuestoCrud.cargar();
    } else {
      setBanner({
        type: 'error',
        message: 'Error en el proceso de gestión de Presupuestos.',
      });
    }
    setPresupuestoModal(MODAL_NONE);
  };

  const handleTipoConceptoCreated = async () => {
    await tipoConceptoCrud.cargar();
    setBanner({ type: 'success', message: 'Se creó el Tipo Concepto con éxito.' });
    setTipoConceptoModal(MODAL_NONE);
  };

  const handleTipoConceptoUpdated = async () => {
    await tipoConceptoCrud.cargar();
    setBanner({ type: 'success', message: 'Se actualizó el Tipo Concepto con éxito.' });
    setTipoConceptoModal(MODAL_NONE);
  };

  const handleTipoConceptoToggle = async () => {
    if (tipoConceptoModal.kind !== 'toggle') return;
    const result = await tipoConceptoCrud.toggleEstado(tipoConceptoModal.item.tpConcepto);
    if (result.ok) {
      setBanner({
        type: 'success',
        message: 'Se actualizó  el Tipo de Concepto con  éxito',
      });
      await tipoConceptoCrud.cargar();
    } else {
      setBanner({
        type: 'error',
        message: 'Error en el proceso de gestión de Tipo de Concepto',
      });
    }
    setTipoConceptoModal(MODAL_NONE);
  };

  const handleConceptoCreated = async () => {
    await conceptoCrud.cargar();
    setBanner({ type: 'success', message: 'Se creó el Concepto con éxito.' });
    setConceptoModal(MODAL_NONE);
  };

  const handleConceptoUpdated = async () => {
    await conceptoCrud.cargar();
    setBanner({ type: 'success', message: 'Se actualizó el Concepto con éxito.' });
    setConceptoModal(MODAL_NONE);
  };

  const handleConceptoToggle = async () => {
    if (conceptoModal.kind !== 'toggle') return;
    const result = await conceptoCrud.toggleEstado(conceptoModal.item.cdConcepto);
    if (result.ok) {
      setBanner({
        type: 'success',
        message: 'Se actualizó  el Concepto con  éxito',
      });
      await conceptoCrud.cargar();
    } else {
      setBanner({
        type: 'error',
        message: 'Error en el proceso de gestión de Concepto',
      });
    }
    setConceptoModal(MODAL_NONE);
  };

  const presupuestoToggleItem = presupuestoModal.item;
  const tipoConceptoToggleItem = tipoConceptoModal.item;
  const conceptoToggleItem = conceptoModal.item;

  return (
    <div className="relative mx-auto w-full max-w-6xl px-5 pb-24 pt-10 sm:px-8 sm:pt-16">
      <SettingsHeader />
      {banner && (
        <div className="mt-8">
          <Banner type={banner.type} message={banner.message} />
        </div>
      )}

      <div className="mt-10 space-y-6">
        <GlassCard className="p-6 sm:p-8">
          <CrudCardHeader
            title="Presupuestos"
            count={presupuestoCrud.items.length}
            onRecargar={presupuestoCrud.cargar}
            onAdd={() => setPresupuestoModal({ kind: 'create' })}
            addLabel="Nuevo presupuesto"
          />
          <PresupuestoTable
            presupuestos={presupuestoCrud.items}
            isLoading={presupuestoCrud.isLoading}
            error={presupuestoCrud.error}
            onView={(item) => setPresupuestoModal({ kind: 'view', item })}
            onEdit={(item) => setPresupuestoModal({ kind: 'edit', item })}
            onToggle={(item) => setPresupuestoModal({ kind: 'toggle', item })}
          />
        </GlassCard>

        <GlassCard className="p-6 sm:p-8">
          <CrudCardHeader
            title="Tipo de Conceptos"
            count={tipoConceptoCrud.items.length}
            onRecargar={tipoConceptoCrud.cargar}
            onAdd={() => setTipoConceptoModal({ kind: 'create' })}
            addLabel="Nuevo tipo concepto"
          />
          <TipoConceptoTable
            items={tipoConceptoCrud.items}
            isLoading={tipoConceptoCrud.isLoading}
            error={tipoConceptoCrud.error}
            onView={(item) => setTipoConceptoModal({ kind: 'view', item })}
            onEdit={(item) => setTipoConceptoModal({ kind: 'edit', item })}
            onToggle={(item) => setTipoConceptoModal({ kind: 'toggle', item })}
          />
        </GlassCard>

        <GlassCard className="p-6 sm:p-8">
          <CrudCardHeader
            title="Conceptos"
            count={conceptoCrud.items.length}
            onRecargar={conceptoCrud.cargar}
            onAdd={() => setConceptoModal({ kind: 'create' })}
            addLabel="Nuevo concepto"
          />
          <ConceptoTable
            items={conceptoCrud.items}
            isLoading={conceptoCrud.isLoading}
            error={conceptoCrud.error}
            onView={(item) => setConceptoModal({ kind: 'view', item })}
            onEdit={(item) => setConceptoModal({ kind: 'edit', item })}
            onToggle={(item) => setConceptoModal({ kind: 'toggle', item })}
          />
        </GlassCard>
      </div>

      <footer className="mt-10 flex flex-col items-center justify-between gap-2 border-t border-white/5 pt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-white/30 sm:flex-row">
        <span>Control de Gastos · FE</span>
        <span>v0.1.0 — build {new Date().getFullYear()}</span>
      </footer>

      <CrearPresupuestoModal
        isOpen={presupuestoModal.kind === 'create'}
        onClose={closePresupuestoModal}
        onCreated={handlePresupuestoCreated}
      />
      <ActualizarPresupuestoModal
        isOpen={presupuestoModal.kind === 'edit'}
        onClose={closePresupuestoModal}
        presupuesto={presupuestoModal.item}
        onUpdated={handlePresupuestoUpdated}
      />
      <VisualizarPresupuestoModal
        isOpen={presupuestoModal.kind === 'view'}
        onClose={closePresupuestoModal}
        presupuesto={presupuestoModal.item}
      />
      <ConfirmarToggleModal
        isOpen={presupuestoModal.kind === 'toggle'}
        onClose={closePresupuestoModal}
        onConfirm={handlePresupuestoToggle}
        item={presupuestoToggleItem}
        itemStatus={presupuestoToggleItem?.stPresupuesto}
        entityName="presupuesto"
        itemLabel={presupuestoToggleItem?.dePresupuesto}
        isSubmitting={presupuestoCrud.isMutating}
      />

      <CrearTipoConceptoModal
        isOpen={tipoConceptoModal.kind === 'create'}
        onClose={closeTipoConceptoModal}
        onCreated={handleTipoConceptoCreated}
      />
      <ActualizarTipoConceptoModal
        isOpen={tipoConceptoModal.kind === 'edit'}
        onClose={closeTipoConceptoModal}
        item={tipoConceptoModal.item}
        onUpdated={handleTipoConceptoUpdated}
      />
      <VisualizarTipoConceptoModal
        isOpen={tipoConceptoModal.kind === 'view'}
        onClose={closeTipoConceptoModal}
        item={tipoConceptoModal.item}
      />
      <ConfirmarToggleModal
        isOpen={tipoConceptoModal.kind === 'toggle'}
        onClose={closeTipoConceptoModal}
        onConfirm={handleTipoConceptoToggle}
        item={tipoConceptoToggleItem}
        itemStatus={tipoConceptoToggleItem?.stTipoConcepto}
        entityName="Tipo Concepto"
        itemLabel={tipoConceptoToggleItem?.deTipoConcepto}
        isSubmitting={tipoConceptoCrud.isMutating}
      />

      <CrearConceptoModal
        isOpen={conceptoModal.kind === 'create'}
        onClose={closeConceptoModal}
        onCreated={handleConceptoCreated}
      />
      <ActualizarConceptoModal
        isOpen={conceptoModal.kind === 'edit'}
        onClose={closeConceptoModal}
        item={conceptoModal.item}
        onUpdated={handleConceptoUpdated}
      />
      <VisualizarConceptoModal
        isOpen={conceptoModal.kind === 'view'}
        onClose={closeConceptoModal}
        item={conceptoModal.item}
      />
      <ConfirmarToggleModal
        isOpen={conceptoModal.kind === 'toggle'}
        onClose={closeConceptoModal}
        onConfirm={handleConceptoToggle}
        item={conceptoToggleItem}
        itemStatus={conceptoToggleItem?.stConcepto}
        entityName="Concepto"
        itemLabel={conceptoToggleItem?.deConcepto}
        isSubmitting={conceptoCrud.isMutating}
      />
    </div>
  );
}

export default Settings;